import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../firebase';

/** Google Calendar — create/update events (task sync). */
export const GOOGLE_CALENDAR_EVENTS_SCOPE =
  'https://www.googleapis.com/auth/calendar.events';

/**
 * Google provider with Calendar scope for task ↔ event sync.
 * Reuse this instance; scopes are applied per sign-in.
 */
export function createGoogleAuthProvider() {
  const provider = new GoogleAuthProvider();
  provider.addScope(GOOGLE_CALENDAR_EVENTS_SCOPE);
  provider.setCustomParameters({
    prompt: 'consent',
    access_type: 'offline',
  });
  return provider;
}

/**
 * Sign in with Google and request Calendar events scope.
 * @returns {Promise<import('firebase/auth').UserCredential>}
 */
export async function signInWithGoogleCalendar() {
  return signInWithPopup(auth, createGoogleAuthProvider());
}

/**
 * OAuth2 access token for Google APIs (Calendar, etc.).
 * Use this — not Firebase `getIdToken()` — for Calendar API requests.
 *
 * @param {import('firebase/auth').UserCredential} userCredential
 * @returns {string|null}
 */
export function getGoogleAccessTokenFromCredential(userCredential) {
  const credential = GoogleAuthProvider.credentialFromResult(userCredential);
  return credential?.accessToken ?? null;
}

/**
 * Re-request Google OAuth tokens (e.g. link Calendar for email/password users).
 * @param {import('firebase/auth').User} user
 * @returns {Promise<import('firebase/auth').UserCredential>}
 */
export async function linkGoogleCalendarScope(user) {
  const { reauthenticateWithPopup } = await import('firebase/auth');
  return reauthenticateWithPopup(user, createGoogleAuthProvider());
}

/**
 * Extract Google OAuth tokens from a Firebase Google sign-in / link result.
 * Refresh tokens are only returned when `access_type=offline` and the user
 * grants consent; Firebase exposes them on `_tokenResponse` (undocumented).
 *
 * @param {import('firebase/auth').UserCredential} userCredential
 * @returns {{ accessToken: string|null, idToken: string|null, refreshToken: string|null }}
 */
export function extractGoogleOAuthTokens(userCredential) {
  const credential = GoogleAuthProvider.credentialFromResult(userCredential);
  const accessToken = credential?.accessToken ?? null;
  const idToken = credential?.idToken ?? null;

  const tokenResponse = userCredential?._tokenResponse;
  const refreshToken =
    (typeof tokenResponse?.refreshToken === 'string' && tokenResponse.refreshToken) ||
    (typeof credential?.refreshToken === 'string' && credential.refreshToken) ||
    null;

  return { accessToken, idToken, refreshToken };
}
